import React from "react"
import Typography from "aldoo-ra/Typography"
import { twMerge } from "tailwind-merge"
import { ChevronRightIcon } from "@heroicons/react/24/outline"

/**
 * KPI Card component for displaying statistics in the customer support dashboard
 *
 * @param {Object} props
 * @param {string} props.title - Title of the KPI card
 * @param {string|number} props.value - Value to display
 * @param {string} props.bgColor - Background color class
 * @param {string} props.textColor - Text color for the value
 * @param {string} props.titleColor - Text color for the title
 * @param {boolean} props.isHighlighted - Whether to highlight this card
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {Function} props.onClick - Click handler function
 * @param {boolean} props.isClickable - Whether the card is clickable
 */
const KPICard = ({
  title,
  value,
  bgColor = "bg-admin_paper",
  textColor = "text-admin_text_secondary",
  titleColor = "text-admin_text",
  isHighlighted = false,
  icon,
  onClick,
  isClickable = false,
}) => {
  const handleClick = () => {
    if (isClickable && onClick) {
      onClick()
    }
  }

  return (
    <div
      className={twMerge(
        bgColor,
        "h-[165px] rounded-xl p-6 lg:p-5 xl:p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-200",
        isHighlighted ? "border border-admin_error" : "",
        isClickable ? "cursor-pointer hover:shadow-md" : ""
      )}
      onClick={handleClick}
      role={isClickable ? "button" : undefined}
      aria-label={isClickable ? `View ${title} tickets` : undefined}
    >
      <div className="flex justify-between">
        <Typography
          component="h2"
          variant="admin_h4"
          className={twMerge(titleColor, "font-medium leading-none")}
        >
          {title}
        </Typography>

        {/* Visual indicator for clickable cards */}
        {isClickable && (
            < ChevronRightIcon className="w-5 h-5 inline-block text-admin_text_secondary flex-shrink-0 mt-0.5 lg:mt-[3px]" />
        )}
      </div>

      <div className="flex items-center justify-between">
        <Typography 
          component="output" 
          variant="admin_h2" 
          className={twMerge(textColor, "md:text-[28px] lg:text-[28px] xl:text-[36px] leading-none")}
        >
          {value}
        </Typography>

        {icon && <div className="w-10 xl:w-14 h-10 xl:h-14 flex items-center justify-center bg-admin_canvas rounded-lg">{icon}</div>}
      </div>

      {/* Removed the "clickable" text to preserve layout when title goes in 2 rows */}
      {/* {isClickable && (
        <Typography
          variant="admin_txt2"
          className="text-admin_text_secondary mt-3 text-xs italic"
        >
          Click to view
        </Typography>
      )} */}
    </div>
  )
}

export default KPICard
